import { CornerStyle } from 'context/ThemeContext/ThemeContext';
import React, { useEffect, useRef } from 'react';
import CatchLinkWrapper from 'components/wrappers/CatchLinkWrapper';
import { ExperienceData, LinkTrackingParams } from 'utils/linkTracking';

type HtmlWrapperProps = {
  html?: string;
  styles?: string;
  background?: string;
  hideAnimation?: boolean;
  paragraphTextColor?: string;
  cornerStyle?: string;
  linkTrackingParams?: LinkTrackingParams | null;
  experienceData?: ExperienceData | null;
  onLinkClick?: (href: string) => void;
};

// const fontModifier = (html: string = ''): string =>
//   html.replaceAll(
//     'style="font-family:',
//     'class="override-font" style="font-family:'
//   );

// Inject scrollbar styles into document head (runs once)
// Only applies to elements inside .custom-horizontal-scroll containers
const SCROLLBAR_STYLE_ID = 'html-wrapper-scrollbar-styles';
if (
  typeof document !== 'undefined' &&
  !document.getElementById(SCROLLBAR_STYLE_ID)
) {
  const styleEl = document.createElement('style');
  styleEl.id = SCROLLBAR_STYLE_ID;
  styleEl.textContent = `
    /* Hide native scrollbar and use custom indicator instead - only for custom-horizontal-scroll */
    .custom-horizontal-scroll div[style*="overflow-x"]::-webkit-scrollbar {
      display: none !important;
      height: 0 !important;
      width: 0 !important;
    }
    .custom-horizontal-scroll div[style*="overflow-x"] {
      -ms-overflow-style: none !important;
      scrollbar-width: none !important;
    }
  `;
  document.head.appendChild(styleEl);
}

const HtmlWrapper: React.FC<HtmlWrapperProps> = ({
  html,
  styles,
  background,
  hideAnimation,
  paragraphTextColor,
  cornerStyle,
  linkTrackingParams,
  experienceData,
  onLinkClick,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Add custom scroll indicators to scrollable elements with .custom-horizontal-scroll class
  useEffect(() => {
    if (!wrapperRef.current) return;

    // Only run if custom-horizontal-scroll class exists in the HTML
    if (!html?.includes('custom-horizontal-scroll')) return;

    const addIndicatorToElement = (scrollEl: HTMLElement) => {
      try {
        // Skip if indicator already exists
        if (scrollEl.dataset?.hasIndicator === 'true') {
          return;
        }
        scrollEl.dataset.hasIndicator = 'true';

        // Create scroll indicator container (the track)
        const indicatorContainer = document.createElement('div');
        indicatorContainer.className = 'scroll-indicator-container';
        indicatorContainer.style.cssText = `
          width: 100%;
          height: 4px;
          background-color: #e5e7eb;
          border-radius: 2px;
          margin-top: 8px;
          position: relative;
          overflow: hidden;
        `;

        // Create scroll indicator thumb
        const indicatorThumb = document.createElement('div');
        indicatorThumb.className = 'scroll-indicator-thumb';
        indicatorThumb.style.cssText = `
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          background-color: #9ca3af;
          border-radius: 2px;
          transition: left 0.05s ease-out, width 0.05s ease-out;
        `;

        indicatorContainer.appendChild(indicatorThumb);

        // Insert after the scrollable element
        if (scrollEl.parentNode) {
          scrollEl.parentNode.insertBefore(
            indicatorContainer,
            scrollEl.nextSibling
          );
        }

        // Update indicator position
        const updateIndicator = () => {
          const scrollWidth = scrollEl.scrollWidth;
          const clientWidth = scrollEl.clientWidth;
          const scrollLeft = scrollEl.scrollLeft;

          if (scrollWidth <= clientWidth) {
            indicatorContainer.style.display = 'none';
            return;
          }

          indicatorContainer.style.display = 'block';
          const thumbWidthPercent = Math.max(
            (clientWidth / scrollWidth) * 100,
            20
          );
          const maxScroll = scrollWidth - clientWidth;
          const scrollPercent = maxScroll > 0 ? scrollLeft / maxScroll : 0;
          const thumbLeftPercent = scrollPercent * (100 - thumbWidthPercent);

          indicatorThumb.style.width = `${thumbWidthPercent}%`;
          indicatorThumb.style.left = `${thumbLeftPercent}%`;
        };

        // Initial update after a small delay to ensure layout is complete
        requestAnimationFrame(() => {
          updateIndicator();
        });

        // Update on scroll
        scrollEl.addEventListener('scroll', updateIndicator, { passive: true });

        // Update on resize
        const resizeObserver = new ResizeObserver(updateIndicator);
        resizeObserver.observe(scrollEl);
      } catch (error) {
        console.error('Error adding scroll indicator:', error);
      }
    };

    const processScrollableElements = () => {
      try {
        if (!wrapperRef.current) return;
        // Only target scrollable elements inside .custom-horizontal-scroll containers
        const scrollableElements =
          wrapperRef.current.querySelectorAll<HTMLElement>(
            '.custom-horizontal-scroll div[style*="overflow-x"]'
          );
        scrollableElements.forEach(addIndicatorToElement);
      } catch (error) {
        console.error('Error processing scrollable elements:', error);
      }
    };

    // Process initially
    processScrollableElements();

    // Also observe for dynamically added elements
    const observer = new MutationObserver(() => {
      processScrollableElements();
    });

    observer.observe(wrapperRef.current, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      // Clean up indicators - only inside .custom-horizontal-scroll containers
      if (wrapperRef.current) {
        try {
          const containers = wrapperRef.current.querySelectorAll(
            '.custom-horizontal-scroll'
          );
          containers.forEach((container) => {
            const indicators = container.querySelectorAll(
              '.scroll-indicator-container'
            );
            indicators.forEach((el) => el.remove());
            const scrollables = container.querySelectorAll<HTMLElement>(
              'div[style*="overflow-x"]'
            );
            scrollables.forEach((el) => {
              delete el.dataset.hasIndicator;
            });
          });
        } catch (error) {
          console.error('Error cleaning up indicators:', error);
        }
      }
    };
  }, [html]);

  const getCornerStyle = () => {
    switch (cornerStyle) {
      case CornerStyle.FULL_ROUND:
        return 'rounded-[26px]';
      case CornerStyle.ROUNDED_CORNERS:
        return 'rounded-[10px]';
      case CornerStyle.SQUARE_CORNERS:
        return 'rounded-none';
      default:
        return 'rounded-[26px]';
    }
  };

  const getTableStyle = () => {
    if (html?.includes('fr-alternate-rows'))
      return 'even:[&_tr]:bg-gray-100 odd:[&_tr]:bg-white';
    else return '';
  };

  const content = (
    <>
      <div
        ref={wrapperRef}
        tabIndex={0}
        dangerouslySetInnerHTML={{ __html: html || '' }}
        style={{
          animation: hideAnimation ? '' : 'fade-in 0.6s ease-in-out forwards',
          color: paragraphTextColor ? paragraphTextColor : 'inherit',
          background: background ? background : 'auto',
          overflow: 'visible',
        }}
        className={`
          relative w-full flex flex-col break-words whitespace-pre-wrap animate-htmlAnimation 
          [&_a]:underline 
          [&_video]:bg-black [&_th]:text-left 
          [&_pre]:font-mono [&_pre]:whitespace-normal 
          [&_span]:text-inherit [&_span]:leading-inherit [&_span]:!bg-no-repeat [&_span]:!bg-center 
          [&_hr]:w-full [&_hr]:h-px [&_hr]:my-1.5 [&_hr]:bg-gray-300 
          [&_ul]:text-xs [&_ul]:leading-[18px] [&_ul]:pl-6 [&_ul]:list-disc 
          [&_ol]:text-xs [&_ol]:leading-[18px] [&_ol]:pl-6 [&_ol]:list-decimal 
          [&_p]:text-xs [&_p]:m-0 [&_p]:p-0 [&_p]:text-inherit 
          [&_.fr-fil]:float-left [&_.fr-fir]:float-right [&_.fr-fic]:mx-auto 
          [&_.fr-box]:m-0 [&_.fr-basic]:m-0 [&_.fr-element]:m-0 
          [&_.fr-size-large]:mt-5 [&_.fr-size-large]:w-full [&_.fr-size-large]:h-[52px] [&_.fr-size-large>a]:text-center 
          [&_.fr-size-large]:flex [&_.fr-size-large]:justify-center [&_.fr-size-large]:items-center 
          [&_.fr-size-large]:cursor-pointer [&_.fr-size-large]:font-medium [&_.fr-size-large]:text-lg 
          [&_.fr-size-large]:decoration-0 [&_.fr-size-large]:bg-[#0A0B0A] [&_.fr-size-large]:text-white [&_.fr-size-large]:no-underline 
          [&_.fr-bordered]:!w-[calc(100%-16px)] [&_.fr-bordered]:ring-8 [&_.fr-bordered]:ring-gray-300 
          [&_h1]:!text-inherit [&_h1]:!text-[32px] [&_h1]:!font-bold [&_h1]:!leading-normal 
          [&_h1>*]:text-inherit [&_h1>*]:text-[32px] [&_h1>*]:font-bold [&_h1>*]:leading-normal 
          [&_h2]:!text-inherit [&_h2]:!text-[24px] [&_h2]:!font-bold [&_h2]:!leading-normal 
          [&_h2>*]:text-inherit [&_h2>*]:text-[24px] [&_h2>*]:font-bold [&_h2>*]:leading-normal 
          [&_h3]:!text-inherit [&_h3]:!text-[18px] [&_h3]:!font-bold [&_h3]:!leading-normal 
          [&_h3>*]:text-inherit [&_h3>*]:text-[18px] [&_h3>*]:font-bold [&_h3>*]:leading-normal 
          [&_h4]:!text-inherit [&_h4]:!text-[14px] [&_h4]:!font-bold [&_h4]:!leading-normal 
          [&_h4>*]:text-inherit [&_h4>*]:text-[14px] [&_h4>*]:font-bold [&_h4>*]:leading-normal 
          [&_h5]:!text-inherit [&_h5]:!leading-normal 
          [&_h5>*]:text-inherit [&_h5>*]:leading-normal 
          [&_h6]:!text-inherit [&_h6]:!leading-normal 
          [&_h6>*]:text-inherit [&_h6>*]:leading-normal 
          [&_.fr-inner]:block [&_.fr-inner]:text-center 
          [&_.fr-text-spaced]:tracking-[0.1em] [&_.fr-text-uppercase]:uppercase 
          [&_.fr-text-bordered]:border [&_.fr-text-bordered]:border-solid [&_.fr-text-bordered]:border-l-0 [&_.fr-text-bordered]:border-r-0 
          [&_.fr-text-bordered]:py-2 [&_.fr-text-bordered]:border-black/50 
          [&_.fr-text-gray]:m-0 [&_.fr-text-gray]:p-0 [&_.fr-text-gray]:text-[#7D7D7D] 
          [&_.fr-rounded]:my-1 [&_.fr-rounded]:rounded-[10px] [&_.fr-shadow]:shadow-md 
          [&_iframe]:!relative [&_iframe]:!aspect-video [&_iframe]:!max-w-full [&_iframe]:!h-auto 
          [&_.fr-video]:relative [&_.fr-video>video]:mx-auto 
          [&_.fr-video>.shadow]:w-[calc(100%-14px)] [&_.fr-video>.shadow]:shadow-froalaVideo [&_.fr-video>.shadow]:mx-0 
          [&_.fr-video>.border]:w-[calc(100%-8px)] [&_.fr-video>.border]:ml-1 [&_.fr-video>.border]:ring-4 [&_.fr-video>.border]:ring-froalaBorder 
          [&_.fr-video>.border-radius]:rounded-xl 
          [&_.fr-fvl>video]:float-left [&_.fr-fvr>video]:float-right [&_.fr-active>video]:mx-auto 
          [&_.ql-video]:absolute [&_.ql-video]:top-0 [&_.ql-video]:left-0 [&_.ql-video]:w-full [&_.ql-video]:h-full 
          [&_.ql-align-right]:text-right [&-.ql-align-right]:w-full 
          [&_.ql-align-left]:text-left [&-.ql-align-left]:w-full 
          [&_.ql-align-center]:text-center [&-.ql-align-center]:w-full 
          [&_.ql-align-justify]:text-justify [&-.ql-align-justify]:w-full 
          [&_.ql-size-large]:flex [&_.ql-size-large]:items-center [&_.ql-size-large]:justify-center 
          [&_.ql-size-large]:cursor-pointer [&_.ql-size-large]:rounded-[26px] [&_.ql-size-large]:decoration-0 
          [&_.ql-size-large>a]:text-center [&_.ql-size-large]:mt-5 [&_.ql-size-large]:w-full [&_.ql-size-large]:h-[54px] 
          [&_.ql-size-large]:font-medium [&_.ql-size-large]:text-lg [&_.ql-size-large]:bg-black [&_.ql-size-large]:text-white 
          ${getCornerStyle()} ${getTableStyle()} ${styles}
        `}
      />
    </>
  );

  if (!linkTrackingParams?.enabled || !experienceData) {
    return content;
  }

  return (
    <CatchLinkWrapper
      onLinkClick={onLinkClick || (() => {})}
      linkTrackingParams={linkTrackingParams}
      experienceData={experienceData}
    >
      {content}
    </CatchLinkWrapper>
  );
};

export default HtmlWrapper;
